import API_CONFIG from '../config/api';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useDemoMode } from '../hooks/useDemoMode';
import CSVConfigDialog from './CSVConfigDialog';
import './PhotoCollection.css';

// Function to determine file icon based on extension and type
const getFileIcon = (item) => {
  if (!item.name) return '📄';
  
  const extension = item.name.toLowerCase().split('.').pop();
  
  // Photo collections (special case)
  if (item.photoCollection) {
    return '📷';
  }
  
  // CSV files
  if (extension === 'csv') {
    return '📄';
  }
  
  // Raster data (images/GIS rasters)
  if (['tiff', 'tif', 'geotiff', 'jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) {
    return '🖼️';
  }
  
  // Vector/Spatial data
  if (['shp', 'kmz', 'kml', 'geojson', 'json', 'gpx', 'gdb'].includes(extension)) {
    return '🗺️';
  }
  
  // Default file icon
  return '📄';
};

const TreeBrowser = ({ onAddLayer, activeLayers, onSyncComplete, sharepointState, setSharepointState }) => {
  const { makeAuthenticatedRequest, isAuthenticated } = useAuth();
  const { isDemoMode } = useDemoMode();
  const [loadingFolders, setLoadingFolders] = useState(new Set());
  const [syncing, setSyncing] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Precaching state
  const [precachedFolders, setPrecachedFolders] = useState(new Map());
  const [precachingQueue, setPrecachingQueue] = useState(new Set());
  const precacheTimeoutRefs = useRef(new Map());
  const [csvConfigDialog, setCsvConfigDialog] = useState({
    isOpen: false,
    csvInfo: null,
    datasetId: null
  });
  const [searchFilter, setSearchFilter] = useState('');
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [isLoadingDrives, setIsLoadingDrives] = useState(false);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const fetchDatasetsTimeoutRef = useRef(null);

  // Supported file extensions
  const supportedExtensions = ['.geojson', '.shp', '.csv', '.kml', '.kmz', '.tif', '.tiff', '.gpx', '.gml'];
  
  // Image file extensions for photo collections
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.tiff', '.heic', '.webp', '.gif'];

  // Extract state from persistent store
  const {
    sites,
    selectedSite,
    drives,
    selectedDrive,
    treeData,
    expandedFolders,
    datasets,
    isInitialized
  } = sharepointState;

  useEffect(() => {
    if (!isInitialized && isAuthenticated) {
      const timer = setTimeout(() => {
        initializeData();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isInitialized, isAuthenticated]);

  // Load remembered site from localStorage
  useEffect(() => {
    if (sites.length > 0 && !selectedSite) {
      const rememberedSiteId = localStorage.getItem('sharespace-selected-site-id');
      if (rememberedSiteId) {
        const rememberedSite = sites.find(site => site.id === rememberedSiteId);
        if (rememberedSite) {
          console.log('Restoring previously selected site:', rememberedSite.name);
          setSharepointState(prev => ({
            ...prev,
            selectedSite: rememberedSite
          }));
          return;
        }
      }
      
      // Fall back to first site if no remembered site found
      setSharepointState(prev => ({
        ...prev,
        selectedSite: sites[0]
      }));
    }
  }, [sites, selectedSite]);

  // Save selected site to localStorage whenever it changes
  useEffect(() => {
    if (selectedSite) {
      localStorage.setItem('sharespace-selected-site-id', selectedSite.id);
      localStorage.setItem('sharespace-selected-site-name', selectedSite.name);
      console.log('Remembered site selection:', selectedSite.name);
    }
  }, [selectedSite]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (fetchDatasetsTimeoutRef.current) {
        clearTimeout(fetchDatasetsTimeoutRef.current);
      }
      
      // Clear all precache hover timeouts
      precacheTimeoutRefs.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      precacheTimeoutRefs.current.clear();
    };
  }, []);

  // Utility function to clear remembered site (for debugging or user preference)
  const clearRememberedSite = () => {
    localStorage.removeItem('sharespace-selected-site-id');
    localStorage.removeItem('sharespace-selected-site-name');
    console.log('Cleared remembered site selection');
  };

  const initializeData = async () => {
    await fetchSites();
    await fetchDatasets();
    setSharepointState(prev => ({ ...prev, isInitialized: true }));
  };

  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchSites();
      await fetchDatasets();
      // Clear tree data to force reload of folder contents
      setSharepointState(prev => ({ 
        ...prev, 
        treeData: [], 
        expandedFolders: new Set() 
      }));
    } catch (err) {
      setError('Error refreshing data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSite) {
      fetchDrives(selectedSite.id);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite && drives.length > 0 && !isLoadingDrives) {
      console.log('Loading all drive contents...');
      loadAllDriveContents();
    }
  }, [selectedSite, drives]);

  // Re-fetch datasets only when authentication or initialization status changes
  useEffect(() => {
    if (isAuthenticated && isInitialized && datasets.length === 0) {
      fetchDatasets();
    }
  }, [isAuthenticated, isInitialized]);

  const fetchSites = async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      console.log('Skipping sites fetch - user not authenticated');
      return;
    }
    
    try {
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/sharepoint/sites`);
      const data = await response.json();
      
      console.log('Sites API response:', data);
      
      if (data.success) {
        console.log('Number of sites found:', data.data.length);
        console.log('Site names:', data.data.map(site => site.name));
        
        setSharepointState(prev => ({
          ...prev, 
          sites: data.data
        }));
      } else {
        setError(data.error || 'Failed to fetch sites');
      }
    } catch (err) {
      setError('Error connecting to server: ' + err.message);
    }
  };


  const fetchDrives = async (siteId) => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      console.log('Skipping drives fetch - user not authenticated');
      return;
    }
    
    try {
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/sharepoint/sites/${siteId}/drives`);
      const data = await response.json();
      
      if (data.success) {
        setSharepointState(prev => {
          const newState = { ...prev, drives: data.data };
          if (!prev.selectedDrive) {
            const docsFolder = data.data.find(drive => drive.name === 'Documents' || drive.driveType === 'documentLibrary');
            newState.selectedDrive = docsFolder || data.data[0];
          }
          return newState;
        });
      }
    } catch (err) {
      setError('Error fetching drives: ' + err.message);
    }
  };

  const fetchDatasetsImmediate = async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      console.log('Skipping datasets fetch - user not authenticated');
      return;
    }
    
    // Prevent concurrent calls
    if (isLoadingDatasets) {
      return;
    }
    
    setIsLoadingDatasets(true);
    try {
      const response = await makeAuthenticatedRequest(`${API_CONFIG.BASE_URL}/data/datasets`);
      const data = await response.json();
      
      if (data.success) {
        setSharepointState(prev => ({ ...prev, datasets: data.data }));
      } else {
        console.error('Failed to fetch datasets:', data.error);
      }
    } catch (err) {
      console.error('Error fetching datasets:', err);
      // Don't throw the error to prevent retry loops
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  const fetchDatasets = () => {
    // Clear any existing timeout
    if (fetchDatasetsTimeoutRef.current) {
      clearTimeout(fetchDatasetsTimeoutRef.current);
    }
    
    // Set a new timeout (debounce for 300ms)
    fetchDatasetsTimeoutRef.current = setTimeout(() => {
      fetchDatasetsImmediate();
    }, 300);
  };

  const loadAllDriveContents = async () => {
    if (!selectedSite || drives.length === 0 || isLoadingDrives) return;

    setIsLoadingDrives(true);
    console.log('Starting to load drive contents for', drives.length, 'drives');

    try {
      // If there's only one document library, skip the drive level and show folders directly
      if (drives.length === 1) {
        const singleDrive = drives[0];
        console.log('Single document library detected, skipping drive level for:', singleDrive.name);
        
        // Load the drive contents and set them as root level items
        setSharepointState(prev => ({
          ...prev,
          treeData: [] // Clear while loading
        }));
        
        await loadDriveContentsAsRoot(singleDrive, 'root');
      } else {
        // Multiple drives: create tree structure with all drives as top-level folders
        const driveNodes = drives.map(drive => ({
          id: `drive-${drive.id}`,
          name: drive.name,
          type: 'drive',
          drive: drive,
          isExpanded: true, // Auto-expand drives
          children: [],
          loading: false
        }));

        setSharepointState(prev => ({
          ...prev,
          treeData: driveNodes
        }));

        // Load contents for each drive
        for (const drive of drives) {
          await loadDriveContents(drive, 'root');
        }
      }
    } finally {
      setIsLoadingDrives(false);
    }
  };

  const loadDriveContentsAsRoot = async (drive, folderId, parentPath = '') => {
    if (!selectedSite) return;

    setError(null);

    try {
      const encodedDriveId = encodeURIComponent(drive.id);
      const encodedFolderId = encodeURIComponent(folderId);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/folders/${encodedFolderId}/contents`;
      
      console.log('Loading drive contents as root from:', url);
      
      const response = await makeAuthenticatedRequest(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const items = data.data.map(item => ({
          ...item,
          driveId: drive.id,
          parentDriveId: drive.id,
          fullPath: parentPath ? `${parentPath}/${item.name}` : item.name
        }));

        console.log('📁 Loaded drive contents as root:', items.length, 'items');

        // Set items directly as root-level tree data
        setSharepointState(prev => ({
          ...prev,
          treeData: items
        }));
      }
    } catch (err) {
      console.error('Error loading drive contents as root:', err);
      setError(`Error loading ${drive.name}: ${err.message}`);
    }
  };

  const loadDriveContents = async (drive, folderId, parentPath = '') => {
    if (!selectedSite) return;

    const driveNodeId = `drive-${drive.id}`;
    setLoadingFolders(prev => new Set([...prev, driveNodeId]));
    setError(null);

    try {
      const encodedDriveId = encodeURIComponent(drive.id);
      const encodedFolderId = encodeURIComponent(folderId);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/folders/${encodedFolderId}/contents`;
      
      console.log('Loading drive contents from:', url);
      
      const response = await makeAuthenticatedRequest(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const items = data.data.map(item => ({
          ...item,
          driveId: drive.id,
          parentDriveId: drive.id,
          fullPath: parentPath ? `${parentPath}/${item.name}` : item.name
        }));

        console.log('📁 Loaded folder contents:', items.length, 'items');
        console.log('📊 Items breakdown:', items.map(item => ({
          name: item.name,
          type: item.type,
          isFile: item.isFile,
          isFolder: item.isFolder
        })));

        setSharepointState(prev => {
          // Items go under the drive node
          const newTreeData = prev.treeData.map(node => {
            if (node.id === driveNodeId) {
              return {
                ...node,
                children: items,
                loading: false
              };
            }
            return node;
          });

          return {
            ...prev,
            treeData: newTreeData
          };
        });
      }
    } catch (err) {
      console.error('Error loading drive contents:', err);
      setError(`Error loading ${drive.name}: ${err.message}`);
    } finally {
      setLoadingFolders(prev => {
        const updated = new Set(prev);
        updated.delete(driveNodeId);
        return updated;
      });
    }
  };

  // Precaching functions
  const createCacheKey = (folderId, driveId) => `${folderId}-${driveId}`;

  const isPrecached = (folderId, driveId) => {
    const cacheKey = createCacheKey(folderId, driveId);
    return precachedFolders.has(cacheKey);
  };

  const getPrecachedContent = (folderId, driveId) => {
    const cacheKey = createCacheKey(folderId, driveId);
    return precachedFolders.get(cacheKey);
  };

  const precacheFolderContent = async (folderId, driveId, parentPath = '') => {
    const cacheKey = createCacheKey(folderId, driveId);
    
    // Skip if already cached or currently being loaded
    if (precachedFolders.has(cacheKey) || precachingQueue.has(cacheKey)) {
      return;
    }

    const drive = drives.find(d => d.id === driveId);
    if (!selectedSite || !drive) return;

    // Add to precaching queue
    setPrecachingQueue(prev => new Set([...prev, cacheKey]));

    try {
      const encodedDriveId = encodeURIComponent(drive.id);
      const encodedFolderId = encodeURIComponent(folderId);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/folders/${encodedFolderId}/contents`;
      
      const response = await makeAuthenticatedRequest(url);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const items = data.data.map(item => ({
            ...item,
            driveId: drive.id,
            parentDriveId: drive.id,
            fullPath: parentPath ? `${parentPath}/${item.name}` : item.name
          }));

          // Store in precache
          setPrecachedFolders(prev => new Map([...prev, [cacheKey, {
            items,
            timestamp: Date.now(),
            driveId: drive.id
          }]]));
        }
      }
    } catch (err) {
      console.warn('Precache failed for folder:', folderId, err);
    } finally {
      // Remove from precaching queue
      setPrecachingQueue(prev => {
        const next = new Set(prev);
        next.delete(cacheKey);
        return next;
      });
    }
  };

  const precacheChildFolders = async (parentFolderContents, driveId) => {
    if (!parentFolderContents) return;

    // Find all folder children
    const folderChildren = parentFolderContents.filter(item => 
      item.type === 'folder' || item.isFolder
    );

    // Precache each child folder (limit to 5 simultaneous to avoid API spam)
    const chunkSize = 3;
    for (let i = 0; i < folderChildren.length; i += chunkSize) {
      const chunk = folderChildren.slice(i, i + chunkSize);
      await Promise.all(
        chunk.map(folder => 
          precacheFolderContent(folder.id, driveId, folder.fullPath)
        )
      );
      
      // Small delay between chunks to be respectful to the API
      if (i + chunkSize < folderChildren.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };

  const cleanupPrecacheForFolder = (folderId, driveId) => {
    const cacheKey = createCacheKey(folderId, driveId);
    setPrecachedFolders(prev => {
      const next = new Map(prev);
      next.delete(cacheKey);
      return next;
    });
  };

  // Hover-based precaching
  const handleFolderHover = (folder) => {
    if (!folder || folder.type === 'file') return;
    
    const folderId = folder.id;
    const driveId = folder.driveId || folder.parentDriveId;
    const timeoutKey = `${folderId}-${driveId}`;

    // Clear any existing timeout
    if (precacheTimeoutRefs.current.has(timeoutKey)) {
      clearTimeout(precacheTimeoutRefs.current.get(timeoutKey));
    }

    // Start precaching after 300ms hover
    const timeoutId = setTimeout(() => {
      if (!folder.children && !isPrecached(folderId, driveId)) {
        console.log('Hover precaching folder:', folder.name);
        precacheFolderContent(folderId, driveId, folder.fullPath);
      }
    }, 300);

    precacheTimeoutRefs.current.set(timeoutKey, timeoutId);
  };

  const handleFolderHoverEnd = (folder) => {
    if (!folder || folder.type === 'file') return;
    
    const folderId = folder.id;
    const driveId = folder.driveId || folder.parentDriveId;
    const timeoutKey = `${folderId}-${driveId}`;

    // Clear hover timeout
    if (precacheTimeoutRefs.current.has(timeoutKey)) {
      clearTimeout(precacheTimeoutRefs.current.get(timeoutKey));
      precacheTimeoutRefs.current.delete(timeoutKey);
    }
  };

  const loadFolderContents = async (folderId, driveId, parentPath = '') => {
    const drive = drives.find(d => d.id === driveId);
    if (!selectedSite || !drive) return;

    // Check if content is already precached
    if (isPrecached(folderId, driveId)) {
      console.log('Using precached content for folder:', folderId);
      const cachedData = getPrecachedContent(folderId, driveId);
      
      setSharepointState(prev => {
        const updatedTreeData = updateTreeWithFolderContents(prev.treeData, folderId, cachedData.items, drive.id);
        return { ...prev, treeData: updatedTreeData };
      });

      // Start precaching child folders in background
      setTimeout(() => {
        precacheChildFolders(cachedData.items, driveId);
      }, 100);
      
      return;
    }

    setLoadingFolders(prev => new Set([...prev, folderId]));
    setError(null);

    try {
      const encodedDriveId = encodeURIComponent(drive.id);
      const encodedFolderId = encodeURIComponent(folderId);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/folders/${encodedFolderId}/contents`;
      
      console.log('Loading folder contents from:', url);
      
      const response = await makeAuthenticatedRequest(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();

      if (data.success) {
        const items = data.data.map(item => ({
          ...item,
          driveId: drive.id,
          parentDriveId: drive.id,
          fullPath: parentPath ? `${parentPath}/${item.name}` : item.name
        }));

        console.log('Loaded folder contents:', items.length, 'items for folder ID:', folderId);

        setSharepointState(prev => {
          const updatedTreeData = updateTreeWithFolderContents(prev.treeData, folderId, items, drive.id);
          return { ...prev, treeData: updatedTreeData };
        });
        
        // Start precaching child folders in background
        setTimeout(() => {
          precacheChildFolders(items, driveId);
        }, 200);

        // Force a re-render to ensure photo detection UI shows up  
        setTimeout(() => {
          setSharepointState(prev => {
            // Create a deep copy to force React to re-render
            const newTreeData = prev.treeData.map(item => ({ 
              ...item, 
              children: item.children ? [...item.children] : undefined 
            }));
            return { ...prev, treeData: newTreeData };
          });
        }, 100);
      } else {
        setError(data.error || 'Failed to load folder contents');
      }
    } catch (err) {
      setError('Error loading folder: ' + err.message);
    } finally {
      setLoadingFolders(prev => {
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
    }
  };

  const updateTreeWithFolderContents = (tree, folderId, contents, driveId) => {
    return tree.map(item => {
      if (item.id === folderId && (item.isFolder || item.type === 'folder' || item.type === 'drive')) {
        console.log('📁 Found folder to update:', item.name, 'with', contents.length, 'children');
        console.log('🔧 Drive ID being applied:', driveId);
        // Ensure all children have the driveId property
        const childrenWithDriveId = contents.map(child => ({
          ...child,
          driveId: driveId,
          parentDriveId: driveId
        }));
        console.log('👥 Children after driveId assignment:', childrenWithDriveId.map(c => ({ name: c.name, driveId: c.driveId, type: c.type })));
        
        // Check for photo collection in newly loaded contents
        const photoCheck = detectPhotoFolder(childrenWithDriveId);
        console.log('PHOTO_DETECTION_UPDATE:', item.name, 'result:', photoCheck);
        
        // Store photo collection data directly in the folder item
        const updatedItem = { 
          ...item, 
          children: childrenWithDriveId,
          photoCollection: photoCheck
        };
        
        return updatedItem;
      } else if (item.children) {
        return { ...item, children: updateTreeWithFolderContents(item.children, folderId, contents, driveId) };
      }
      return item;
    });
  };

  const toggleFolder = async (folder) => {
    const isDrive = folder.type === 'drive';
    const isExpanded = isDrive ? folder.isExpanded : expandedFolders.has(folder.id);
    
    if (isDrive) {
      // For drives, update the drive's isExpanded property
      setSharepointState(prev => ({
        ...prev,
        treeData: prev.treeData.map(drive => 
          drive.id === folder.id 
            ? { ...drive, isExpanded: !drive.isExpanded }
            : drive
        )
      }));
    } else {
      // For regular folders, use expandedFolders set
      if (isExpanded) {
        // Cleanup precached content for child folders when collapsing
        const cleanupChildPrecache = (folder) => {
          if (folder.children) {
            folder.children.forEach(child => {
              if (child.type === 'folder' || child.isFolder) {
                cleanupPrecacheForFolder(child.id, child.driveId || child.parentDriveId);
                cleanupChildPrecache(child); // Recursive cleanup
              }
            });
          }
        };
        cleanupChildPrecache(folder);

        setSharepointState(prev => {
          const newExpandedFolders = new Set(prev.expandedFolders);
          newExpandedFolders.delete(folder.id);
          return { ...prev, expandedFolders: newExpandedFolders };
        });
      } else {
        setSharepointState(prev => ({
          ...prev,
          expandedFolders: new Set([...prev.expandedFolders, folder.id])
        }));
        
        // Load folder contents if not already loaded
        if (!folder.children) {
          console.log('Loading folder contents for:', folder.name, 'ID:', folder.id, 'DriveID:', folder.driveId || folder.parentDriveId);
          await loadFolderContents(folder.id, folder.driveId || folder.parentDriveId, folder.fullPath);
        }
      }
    }
  };

  const syncFile = async (file) => {
    if (!selectedSite || !file.driveId) return;
    
    setSyncing(prev => ({ ...prev, [file.id]: true }));
    setError(null);
    
    try {
      const encodedDriveId = encodeURIComponent(file.driveId);
      const encodedFileId = encodeURIComponent(file.id);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/files/${encodedFileId}/sync`;
      
      const response = await makeAuthenticatedRequest(url, { method: 'POST' });
      const data = await response.json();
      
      if (data.success) {
        await fetchDatasets();
        
        // Check if this is a CSV file that requires configuration
        if (data.data.requiresConfiguration && data.data.csvInfo) {
          setCsvConfigDialog({
            isOpen: true,
            csvInfo: data.data.csvInfo,
            datasetId: data.data.datasetId
          });
        } else {
          if (onSyncComplete) {
            onSyncComplete(data.data);
          }
        }
      } else {
        setError(`Failed to sync ${file.name}: ${data.error}`);
      }
    } catch (err) {
      setError(`Error syncing ${file.name}: ${err.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [file.id]: false }));
    }
  };

  const syncAndAddToMap = async (file) => {
    console.log('🔵 syncAndAddToMap called with:', { file, selectedSite, isDemoMode });
    
    if (!selectedSite || !file.driveId) {
      console.log('❌ Missing selectedSite or file.driveId:', { selectedSite, driveId: file.driveId });
      return;
    }
    
    setSyncing(prev => ({ ...prev, [file.id]: true }));
    setError(null);
    
    try {
      const encodedDriveId = encodeURIComponent(file.driveId);
      const encodedFileId = encodeURIComponent(file.id);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/files/${encodedFileId}/sync`;
      
      console.log('🔵 Making sync request to:', url);
      const response = await makeAuthenticatedRequest(url, { method: 'POST' });
      console.log('🔵 Sync response status:', response.status);
      const data = await response.json();
      console.log('🔵 Sync response data:', data);
      
      if (data.success) {
        await fetchDatasets();
        
        // Check if this is a CSV file that requires configuration
        if (data.data.requiresConfiguration && data.data.csvInfo) {
          setCsvConfigDialog({
            isOpen: true,
            csvInfo: data.data.csvInfo,
            datasetId: data.data.datasetId
          });
        } else {
          // Let onSyncComplete handle adding the layer to prevent duplicates
          if (onSyncComplete) {
            onSyncComplete(data.data);
          }
        }
      } else {
        setError(`Failed to sync ${file.name}: ${data.error}`);
      }
    } catch (err) {
      setError(`Error syncing ${file.name}: ${err.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [file.id]: false }));
    }
  };

  const syncPhotoCollection = async (folder) => {
    if (!selectedSite || !folder.driveId) {
      console.error('Missing selectedSite or folder.driveId:', { selectedSite, driveId: folder.driveId });
      return;
    }
    
    const photoSyncId = `${folder.id}-photos`;
    setSyncing(prev => ({ ...prev, [photoSyncId]: true }));
    setError(null);
    
    try {
      const encodedDriveId = encodeURIComponent(folder.driveId);
      const encodedFolderId = encodeURIComponent(folder.id);
      const encodedSiteId = encodeURIComponent(selectedSite.id);
      const url = `${API_CONFIG.BASE_URL}/sharepoint/sites/${encodedSiteId}/drives/${encodedDriveId}/folders/${encodedFolderId}/sync-photos`;
      
      console.log('🔵 Making photo collection sync request to:', url);
      const response = await makeAuthenticatedRequest(url, { method: 'POST' });
      console.log('🔵 Photo sync response status:', response.status);
      const data = await response.json();
      console.log('🔵 Photo sync response data:', data);
      
      if (data.success) {
        await fetchDatasets();
        
        // Create a photo layer dataset object for the map
        const photoDataset = {
          id: data.data.datasetId,
          name: `Photos from ${folder.name}`,
          geometry_type: data.data.geometryType,
          feature_count: data.data.recordCount,
          file_name: `${folder.name}_photos`
        };
        
        if (onAddLayer) {
          onAddLayer(photoDataset);
        }
        
        if (onSyncComplete) {
          onSyncComplete({
            datasetId: data.data.datasetId,
            recordCount: data.data.recordCount,
            geometryType: data.data.geometryType
          });
        }
      } else {
        setError(`Failed to sync photo collection from ${folder.name}: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error syncing photo collection:', err);
      setError(`Error syncing photo collection from ${folder.name}: ${err.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [photoSyncId]: false }));
    }
  };

  const isFileSynced = (fileName) => {
    return datasets.some(dataset => dataset.file_name === fileName);
  };

  const getDatasetForFile = (fileName) => {
    return datasets.find(dataset => dataset.file_name === fileName);
  };

  const handleCSVConfigSave = async (configuration) => {
    try {
      console.log('TreeBrowser - Sending CSV configuration:', configuration);
      const response = await makeAuthenticatedRequest(
        `/api/data/datasets/${csvConfigDialog.datasetId}/configure-csv`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(configuration)
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        // First close the dialog
        setCsvConfigDialog({ isOpen: false, csvInfo: null, datasetId: null });
        
        // Wait for the background task to complete
        console.log('CSV processing started, waiting for completion...');
        await waitForCSVProcessing(data.datasetId);
        
        // Refresh the dataset list to show updated status
        fetchDatasets();
        
        // Get the updated dataset info
        const updatedDataset = await getDatasetInfo(data.datasetId);
        
        // Finally call onSyncComplete to show "Add to Map"
        if (onSyncComplete && updatedDataset) {
          onSyncComplete({
            datasetId: data.datasetId,
            recordCount: updatedDataset.feature_count
          });
        }
      } else {
        setError(`Failed to configure CSV: ${data.error}`);
      }
    } catch (err) {
      setError(`Error configuring CSV: ${err.message}`);
    }
  };

  const waitForCSVProcessing = async (datasetId) => {
    const maxAttempts = 30; // 30 seconds max
    const pollInterval = 1000; // 1 second
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/data/datasets/${datasetId}/metadata`
        );
        const data = await response.json();
        
        if (data.success && data.data) {
          const dataset = data.data;
          console.log(`CSV processing status: ${dataset.sync_status}, requires_configuration: ${dataset.requires_configuration}`);
          
          if (dataset.sync_status === 'completed' && !dataset.requires_configuration) {
            console.log('CSV processing completed successfully!');
            return;
          } else if (dataset.sync_status === 'failed' || dataset.sync_status === 'error') {
            throw new Error('CSV processing failed');
          }
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (err) {
        console.error('Error polling CSV processing status:', err);
        if (attempt === maxAttempts - 1) {
          throw err;
        }
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }
    
    throw new Error('CSV processing timed out');
  };

  const getDatasetInfo = async (datasetId) => {
    try {
      const response = await makeAuthenticatedRequest(
        `/api/data/datasets/${datasetId}/metadata`
      );
      const data = await response.json();
      return data.success ? data.data : null;
    } catch (err) {
      console.error('Error getting dataset info:', err);
      return null;
    }
  };

  const handleCSVConfigClose = () => {
    setCsvConfigDialog({ isOpen: false, csvInfo: null, datasetId: null });
  };

  const isLayerActive = (dataset) => {
    return activeLayers.some(layer => layer.id === dataset.id);
  };

  // Helper functions for filtering

  const isFileSupported = (fileName) => {
    const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return supportedExtensions.includes(ext);
  };

  const isImageFile = (fileName) => {
    const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(ext);
  };

  const detectPhotoFolder = (folderContents) => {
    if (!folderContents || !Array.isArray(folderContents)) {
      return null;
    }
    
    const imageFiles = folderContents.filter(item => {
      const isFile = (item.isFile || item.type === 'file');
      const hasImageExt = isImageFile(item.name);
      return isFile && hasImageExt;
    });
    
    
    if (imageFiles.length === 0) return null;
    
    // Return photo collection info
    const photoCollection = {
      totalImages: imageFiles.length,
      imageFiles: imageFiles,
      // Note: We'll need backend support to determine which are geotagged
      geotaggedCount: imageFiles.length // Placeholder - backend will provide actual count
    };
    
    return photoCollection;
  };

  const shouldShowItem = (item) => {
    // Always show folders/drives
    if (item.isFolder || item.type === 'folder' || item.type === 'drive') {
      return true;
    }
    
    // For files, only show supported GIS files and apply search filter
    if (item.isFile || item.type === 'file') {
      // Always filter to supported files only
      if (!isFileSupported(item.name)) {
        return false;
      }
      
      // Check search filter
      if (searchFilter && !item.name.toLowerCase().includes(searchFilter.toLowerCase())) {
        return false;
      }
    }
    
    return true;
  };

  const filterTreeData = (data) => {
    return data.map(item => {
      // Create a copy of the item to avoid mutating original data
      const itemCopy = { ...item };
      
      if (shouldShowItem(item)) {
        // If it's a folder, recursively filter its children
        if (item.children) {
          const filteredChildren = filterTreeData(item.children);
          itemCopy.children = filteredChildren;
          // Show folder if it has visible children or if it matches search itself
          return filteredChildren.length > 0 || 
                 (!searchFilter || item.name.toLowerCase().includes(searchFilter.toLowerCase())) 
                 ? itemCopy : null;
        }
        return itemCopy;
      }
      return null;
    }).filter(item => item !== null);
  };

  const renderTreeItem = (item, level = 0) => {
    const isDrive = item.type === 'drive';
    const isFolder = item.isFolder || item.type === 'folder' || isDrive;
    const isFile = item.isFile || item.type === 'file';
    const isExpanded = isFolder && (isDrive ? item.isExpanded : expandedFolders.has(item.id));
    const isLoading = loadingFolders.has(item.id) || loadingFolders.has(`drive-${item.id}`);
    const isSynced = isFile ? isFileSynced(item.name) : false;
    const dataset = isFile ? getDatasetForFile(item.name) : null;
    const isSyncing = syncing[item.id];
    const isActive = dataset ? isLayerActive(dataset) : false;
    
    // Check if this folder contains photos - use stored photo collection or detect from children
    const photoCollection = item.photoCollection || (isFolder && item.children && item.children.length > 0 ? detectPhotoFolder(item.children) : null);
    const hasPhotos = photoCollection && photoCollection.totalImages > 0;
    
    
    // Debug the photo collection rendering condition

    return (
      <div key={item.id} className="tree-item">
        <div 
          className={`tree-item-content ${isFolder ? 'folder' : 'file'} ${isSynced ? 'synced' : ''} ${isActive ? 'active' : ''} ${isDrive ? 'drive' : ''}`}
          style={{ paddingLeft: `${level * 12 + 8 + (isFile ? 16 : 0)}px` }}
          onMouseEnter={isFolder ? () => handleFolderHover(item) : undefined}
          onMouseLeave={isFolder ? () => handleFolderHoverEnd(item) : undefined}
        >
          {isFolder && (
            <button 
              className={`folder-toggle ${isExpanded ? 'expanded' : ''} ${isLoading ? 'loading' : ''}`}
              onClick={() => !isLoading && toggleFolder(item)}
            >
              {isLoading ? (
                <span className="loading-spinner-small"></span>
              ) : isExpanded ? '▼' : '▶'}
            </button>
          )}
          
          <span className="item-icon">
            {isDrive ? '📚' : isFolder ? '📁' : getFileIcon(item)}
          </span>
          
          <span className="item-name" title={item.fullPath || item.path}>
            {item.name}
          </span>
          
          {isFile && (
            <div className="file-actions">
              
              {isActive ? (
                <span className="active-indicator">🗺️ On Map</span>
              ) : (
                <button
                  onClick={() => {
                    console.log('🔴 Add to Map button clicked!', {
                      isDemoMode,
                      isSynced,
                      dataset,
                      item: item.name
                    });
                    
                    if (isSynced && dataset) {
                      console.log('🟢 Dataset already synced, adding directly to map');
                      // Check if dataset requires configuration (CSV)
                      if (dataset.requires_configuration && dataset.processing_config) {
                        console.log('🟡 Dataset requires configuration, opening dialog');
                        // Show CSV config dialog for existing datasets
                        // Build proper csvInfo structure from stored config
                        const storedConfig = dataset.processing_config;
                        const csvInfo = storedConfig.csv_info || {
                          // Fallback for datasets without proper csv_info
                          columns: storedConfig.columns || [],
                          potentialColumnNames: storedConfig.columns || [],
                          sampleData: storedConfig.preview || [],
                          rawSampleData: storedConfig.preview || [],
                          totalRows: dataset.feature_count || 0,
                          preview: storedConfig.preview || [],
                          delimiter: ','
                        };
                        
                        setCsvConfigDialog({
                          isOpen: true,
                          csvInfo: csvInfo,
                          datasetId: dataset.id
                        });
                      } else {
                        console.log('🟢 Adding dataset to map:', dataset);
                        onAddLayer(dataset);
                      }
                    } else {
                      console.log('🔵 Dataset not synced, calling syncAndAddToMap');
                      syncAndAddToMap(item);
                    }
                  }}
                  disabled={isSyncing}
                  className="btn-add-to-map btn-small"
                >
                  {isSyncing && <span className="button-loading-spinner"></span>}
                  {isSyncing ? 'Adding...' : (isSynced && dataset?.requires_configuration ? 'Configure' : 'Add to Map')}
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Photo Collection Entry */}
        {hasPhotos && isExpanded && (
          <div 
            key={`${item.id}-photos`}
            className="tree-item photo-collection"
            style={{ paddingLeft: `${(level + 1) * 12 + 8}px` }}
          >
            <div className="tree-item-content photo-collection-content">
              <span className="item-icon">📷</span>
              <span className="item-name">
                Photos ({photoCollection.totalImages} images)
              </span>
              <div className="file-actions">
                <button
                  onClick={() => syncPhotoCollection(item)}
                  disabled={syncing[`${item.id}-photos`]}
                  className="btn-add-to-map btn-small photo-collection-btn"
                  title="Add photo points to map"
                >
                  {syncing[`${item.id}-photos`] && <span className="button-loading-spinner"></span>}
                  {syncing[`${item.id}-photos`] ? 'Processing Photos...' : 'Add Photo Layer'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {isFolder && isExpanded && (
          <div className="tree-children">
            {item.children ? (
              item.children.map(child => renderTreeItem(child, level + 1))
            ) : isLoading ? (
              <div className="loading">Loading folder contents...</div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  if (!isAuthenticated && !isDemoMode) {
    return (
      <div className="tree-browser">
        <div className="no-files">
          Please sign in with Microsoft to access SharePoint files.
        </div>
      </div>
    );
  }

  return (
    <div className="tree-browser">
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Site Selection */}
      <div className="site-selection">
        {selectedSite ? (
          <div className="selected-site-clean">
            <div className="site-info">
              <img 
                src="/icons/Microsoft_Office_SharePoint_(2019–present).svg" 
                alt="SharePoint" 
                className="sharepoint-icon-large"
                width="28" 
                height="28" 
              />
              <div className="site-details">
                <span className="site-label">
                  Connected to SharePoint Site ✓
                </span>
                <div className="site-name">
                  {isDemoMode ? 'Data for Thy National Park' : selectedSite.name}
                </div>
              </div>
            </div>
            <div className="site-actions">
              <button
                onClick={() => {
                  const url = isDemoMode ? "https://sharemap.sharepoint.com/:f:/s/ShareShapeDemo/EshPp372nI1AqX6tofLALPEBAHROx-0Ts8__Sz9SzlHQaQ?e=T3YAcT" : selectedSite.webUrl;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                className="btn-visit-sharepoint"
                title="Open SharePoint site in new tab"
              >
                Visit SharePoint Site
                <svg className="external-icon" viewBox="0 0 24 24" width="14" height="14">
                  <path fill="currentColor" d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                </svg>
              </button>
              <button
                onClick={() => isDemoMode ? setShowDemoPopup(true) : setShowSiteSelector(true)}
                className="btn-change-site"
                title="Change SharePoint site"
              >
                Change Site
              </button>
            </div>
          </div>
        ) : (
          <div className="no-site-selected">
            <span className="site-label">SharePoint Site:</span>
            <button
              onClick={() => setShowSiteSelector(true)}
              className="btn-select-site"
              disabled={loading}
            >
              {loading && sites.length === 0 ? 'Loading sites...' : 'Select a site'}
            </button>
          </div>
        )}

        {/* Site Selector Modal/Dropdown */}
        {showSiteSelector && (
          <div className="site-selector-overlay">
            <div className="site-selector-dropdown">
              <div className="site-selector-header">
                <span>Select SharePoint Site</span>
                <button
                  onClick={() => setShowSiteSelector(false)}
                  className="btn-close-selector"
                >
                  ✕
                </button>
              </div>
              <div className="site-options">
                {sites.map(site => (
                  <button
                    key={site.id}
                    onClick={() => {
                      setSharepointState(prev => ({
                        ...prev,
                        selectedSite: site,
                        drives: [],
                        selectedDrive: null,
                        treeData: [],
                        expandedFolders: new Set()
                      }));
                      setShowSiteSelector(false);
                      
                      // Force a re-fetch even if it's the same site
                      setTimeout(() => {
                        fetchDrives(site.id);
                      }, 100);
                    }}
                    className="site-option"
                  >
                    {site.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Demo Popup */}
        {showDemoPopup && (
          <div className="site-selector-overlay">
            <div className="site-selector-dropdown">
              <div className="site-selector-header">
                <span>SharePoint Site Switching</span>
                <button
                  onClick={() => setShowDemoPopup(false)}
                  className="btn-close-selector"
                >
                  ✕
                </button>
              </div>
              <div className="demo-popup-content" style={{ padding: '20px' }}>
                <p><strong>Site switching is not available in the demo.</strong></p>
                <p>This demo is connected to a specific SharePoint site with sample geospatial data to showcase ShareShape's capabilities.</p>
                <p>The demo site contains various file formats including GeoJSON, Shapefiles, and CSV data with geographic information.</p>
                <div className="demo-site-link">
                  <p><strong>Demo SharePoint Site:</strong></p>
                  <a 
                    href="https://sharemap.sharepoint.com/:f:/s/ShareShapeDemo/EshPp372nI1AqX6tofLALPEBAHROx-0Ts8__Sz9SzlHQaQ?e=T3YAcT" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="demo-site-url"
                  >
                    View SharePoint Site
                    <svg className="external-icon" viewBox="0 0 24 24" width="12" height="12">
                      <path fill="currentColor" d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Data Sources Intro */}
      {selectedSite && (
        <div className="data-sources-intro">
          <p>Browse and add geospatial files from your connected SharePoint site to the map.</p>
        </div>
      )}

      {/* File Browser Box */}
      <div className="file-browser-box">
        {/* Controls */}
        <div className="browser-controls">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search files..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Tree View */}
        <div className="tree-section-scrollable">
          {!isInitialized && loading ? (
            <div className="loading">Loading folder structure...</div>
          ) : treeData.length === 0 ? (
            <div className="no-files">
              {selectedSite 
                ? 'Waiting for SharePoint connection'
                : 'Waiting for SharePoint connection'
              }
            </div>
          ) : (
            <div className="tree-container">
              {(() => {
                const filteredData = filterTreeData(treeData);
                
                console.log('🔍 [TreeBrowser] Demo folder flattening debug:', {
                  isDemoMode,
                  filteredDataLength: filteredData.length,
                  filteredData: filteredData.map(item => ({
                    id: item.id,
                    name: item.name,
                    type: item.type,
                    hasChildren: !!item.children,
                    childrenCount: item.children?.length || 0,
                    children: item.children?.map(child => ({
                      id: child.id,
                      name: child.name,
                      isFolder: child.isFolder,
                      hasChildren: !!child.children,
                      childrenCount: child.children?.length || 0
                    })) || []
                  }))
                });
                
                // In demo mode, if there's only one top-level folder, show its contents directly
                if (isDemoMode && filteredData.length === 1) {
                  const topItem = filteredData[0];
                  console.log('🔍 [TreeBrowser] Checking single top item:', {
                    itemType: topItem.type,
                    itemName: topItem.name,
                    isFolder: topItem.isFolder,
                    hasChildren: !!topItem.children,
                    childrenLength: topItem.children?.length
                  });
                  
                  // Handle case where it's a drive with one folder
                  if (topItem.type === 'drive' && topItem.children && topItem.children.length === 1) {
                    const singleFolder = topItem.children[0];
                    console.log('🔍 [TreeBrowser] Drive has single folder:', {
                      folderName: singleFolder.name,
                      isFolder: singleFolder.isFolder,
                      hasChildren: !!singleFolder.children,
                      childrenLength: singleFolder.children?.length
                    });
                    
                    if (singleFolder.isFolder && singleFolder.children) {
                      console.log('✅ [TreeBrowser] Flattening drive->folder contents:', singleFolder.children.map(child => child.name));
                      return singleFolder.children.map(child => renderTreeItem(child, 0));
                    }
                  }
                  
                  // Handle case where the top item is directly a folder (skipped drive level)
                  else if ((topItem.type === 'folder' || topItem.isFolder) && topItem.children) {
                    console.log('✅ [TreeBrowser] Flattening single folder contents:', topItem.children.map(child => child.name));
                    return topItem.children.map(child => renderTreeItem(child, 0));
                  }
                  
                  // If the folder exists but hasn't been loaded yet, try to expand it automatically
                  else if ((topItem.type === 'folder' || topItem.isFolder) && !topItem.children) {
                    console.log('🔄 [TreeBrowser] Auto-expanding single folder:', topItem.name);
                    // Trigger folder expansion
                    setTimeout(() => {
                      toggleFolder(topItem);
                    }, 100);
                  }
                }
                
                console.log('🔍 [TreeBrowser] Using default tree structure');
                // Default behavior: show the normal tree structure
                return filteredData.map(item => renderTreeItem(item));
              })()}
            </div>
          )}
        </div>
      </div>
      
      <CSVConfigDialog
        isOpen={csvConfigDialog.isOpen}
        csvInfo={csvConfigDialog.csvInfo}
        onSave={handleCSVConfigSave}
        onClose={handleCSVConfigClose}
      />
    </div>
  );
};

export default TreeBrowser;